import { useCallback, useState } from 'react';

import type { ArShapeFeature, BlockFeature, DrilledHole, DrillHole } from '~api/services';

/**
 * Manages mutually exclusive selection state for blocks, drilled holes, and AR shapes,
 * including drawer open/close state.
 */
const useSelectionState = () => {
  const [selectedBlock, setSelectedBlock] = useState<BlockFeature | null>(null);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [blockDrawerOpen, setBlockDrawerOpen] = useState(false);

  const [selectedHole, setSelectedHole] = useState<DrilledHole | null>(null);
  const [selectedHoleId, setSelectedHoleId] = useState<string | null>(null);
  const [holeDrawerOpen, setHoleDrawerOpen] = useState(false);

  const [selectedDrillHole, setSelectedDrillHole] = useState<DrillHole | null>(null);
  const [selectedDrillHoleId, setSelectedDrillHoleId] = useState<string | null>(null);
  const [drillHoleDrawerOpen, setDrillHoleDrawerOpen] = useState(false);

  const [selectedArShape, setSelectedArShape] = useState<ArShapeFeature | null>(null);
  const [selectedArShapeId, setSelectedArShapeId] = useState<string | null>(null);
  const [arShapeDrawerOpen, setArShapeDrawerOpen] = useState(false);

  const handleBlockClick = useCallback((block: BlockFeature) => {
    setSelectedHole(null);
    setSelectedHoleId(null);
    setSelectedDrillHole(null);
    setSelectedDrillHoleId(null);
    setSelectedArShape(null);
    setSelectedArShapeId(null);
    setSelectedBlock(block);
    setSelectedBlockId(`${block.properties.xcentre}-${block.properties.ycentre}-${block.properties.zcentre}`);
    setHoleDrawerOpen(false);
    setDrillHoleDrawerOpen(false);
    setArShapeDrawerOpen(false);
    setBlockDrawerOpen(true);
  }, []);

  const handleHoleClick = useCallback((hole: DrilledHole) => {
    setSelectedBlock(null);
    setSelectedBlockId(null);
    setSelectedArShape(null);
    setSelectedArShapeId(null);
    setSelectedHole(hole);
    setSelectedHoleId(hole.hole_name);
    setSelectedDrillHole(null);
    setSelectedDrillHoleId(null);
    setBlockDrawerOpen(false);
    setDrillHoleDrawerOpen(false);
    setArShapeDrawerOpen(false);
    setHoleDrawerOpen(true);
  }, []);

  const handleDrillHoleClick = useCallback((drillHole: DrillHole) => {
    setSelectedBlock(null);
    setSelectedBlockId(null);
    setSelectedHole(null);
    setSelectedHoleId(null);
    setSelectedArShape(null);
    setSelectedArShapeId(null);
    setSelectedDrillHole(drillHole);
    setSelectedDrillHoleId(`${drillHole.pattern_name}/${drillHole.hole_name}`);
    setBlockDrawerOpen(false);
    setHoleDrawerOpen(false);
    setArShapeDrawerOpen(false);
    setDrillHoleDrawerOpen(true);
  }, []);

  const handleArShapeClick = useCallback((shape: ArShapeFeature) => {
    setSelectedBlockId(null);
    setSelectedBlock(null);
    setSelectedHoleId(null);
    setSelectedHole(null);
    setSelectedDrillHoleId(null);
    setSelectedDrillHole(null);
    setSelectedArShape(shape);
    setSelectedArShapeId(String(shape.properties.OBJECTID));
    setBlockDrawerOpen(false);
    setHoleDrawerOpen(false);
    setDrillHoleDrawerOpen(false);
    setArShapeDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setSelectedBlock(null);
    setSelectedBlockId(null);
    setBlockDrawerOpen(false);
    setSelectedHole(null);
    setSelectedHoleId(null);
    setHoleDrawerOpen(false);
    setSelectedDrillHole(null);
    setSelectedDrillHoleId(null);
    setDrillHoleDrawerOpen(false);
    setSelectedArShape(null);
    setSelectedArShapeId(null);
    setArShapeDrawerOpen(false);
  }, []);

  return {
    selectedBlock,
    selectedBlockId,
    blockDrawerOpen,
    selectedHole,
    selectedHoleId,
    holeDrawerOpen,
    selectedDrillHole,
    selectedDrillHoleId,
    drillHoleDrawerOpen,
    selectedArShape,
    selectedArShapeId,
    arShapeDrawerOpen,
    handleBlockClick,
    handleHoleClick,
    handleDrillHoleClick,
    handleArShapeClick,
    handleDrawerClose,
  };
};

export { useSelectionState };
